import React from 'react';
import Tooltip from './common/Tooltip';
import { formatRelativeTime } from '../utils/dateUtils';

/** Human-readable label for risk interpretation */
const interpretationLabel = (key) => {
  const labels = {
    capitulation: 'Capitulation',
    accumulation: 'Accumulation',
    neutral: 'Neutral',
    distribution: 'Distribution',
    bubble_risk: 'Bubble risk',
  };
  return labels[key] || key;
};

/** Human-readable label for component name */
const componentNameLabel = (key) => {
  const labels = {
    rsi: 'RSI',
    fear_greed: 'Fear & Greed',
    ma_deviation: 'MA deviation',
    volatility: 'Volatility',
    divergence: 'Divergence',
    trend: 'Trend',
  };
  return labels[key] || key;
};

/** Human-readable explanation of what the component value means */
const componentExplanation = (name, rawValue, normalized) => {
  switch (name) {
    case 'rsi':
      if (rawValue < 30) return 'oversold';
      if (rawValue <= 70) return 'neutral';
      return 'overbought';
    case 'fear_greed':
      if (rawValue < 20) return 'extreme fear';
      if (rawValue < 40) return 'fear';
      if (rawValue < 60) return 'neutral';
      if (rawValue < 80) return 'greed';
      return 'extreme greed';
    case 'ma_deviation':
      if (rawValue < -15) return 'below 200d MA';
      if (rawValue < 15) return 'near 200d MA';
      return 'above 200d MA';
    case 'volatility':
      if (normalized < 40) return 'low';
      if (normalized < 60) return 'moderate';
      return 'high';
    case 'trend':
      // rawValue is now divergence % (positive = golden cross, negative = death cross)
      if (rawValue > 5) return 'golden cross';
      if (rawValue < -5) return 'death cross';
      return 'neutral';
    case 'divergence':
      if (rawValue < 0) return 'bullish';
      if (rawValue > 0) return 'bearish';
      return 'none';
    default:
      return '';
  }
};

/** Format raw value for display (special cases for some components) */
const formatRawValue = (name, rawValue) => {
  if (typeof rawValue !== 'number') return '—';
  if (name === 'ma_deviation' || name === 'trend') {
    // Show as percentage with sign
    const sign = rawValue >= 0 ? '+' : '';
    return `${sign}${rawValue.toFixed(1)}%`;
  }
  return rawValue.toFixed(1);
};

/** Build tooltip content: name | value | meaning */
const riskScoreTooltipContent = (riskScore) => {
  const components = riskScore.components || [];

  return (
    <span style={{ fontSize: '0.9rem' }}>
      <table style={{ borderCollapse: 'collapse', border: 'none' }}>
        <tbody>
          {components.map((c) => {
            const raw = formatRawValue(c.name, c.rawValue);
            const name = componentNameLabel(c.name);
            const meaning = componentExplanation(c.name, c.rawValue, c.normalized);

            return (
              <tr key={c.name}>
                <td style={{ textAlign: 'right', paddingRight: 6, whiteSpace: 'nowrap' }}>
                  {name}:
                </td>
                <td style={{ textAlign: 'left', whiteSpace: 'nowrap' }}>
                  {raw}
                  {meaning && (
                    <span style={{ color: '#9ca3af', marginLeft: 6 }}>
                      {meaning}
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </span>
  );
};

const riskScoreFillColor = (score) => {
  if (score < 25) return '#22c55e';
  if (score < 45) return '#84cc16';
  if (score < 60) return '#eab308';
  if (score < 75) return '#f97316';
  return '#ef4444';
};

/** 0–100 risk score: score bar + interpretation. Uses Tooltip like other pages (theme dark, size small). */
const RiskScoreBlock = ({ riskScore, tokenName }) => {
  if (!riskScore || typeof riskScore.score !== 'number') return null;
  const score = Math.round(riskScore.score);
  const pct = Math.min(100, Math.max(0, score));
  const relativeTime = riskScore.timestamp ? formatRelativeTime(riskScore.timestamp) : null;
  return (
    <div className="text-sm text-gray-500 dark:text-gray-400">
      <Tooltip
        html={riskScoreTooltipContent(riskScore)}
        position="top"
        trigger="mouseenter focus click"
        hideOnClick="toggle"
        arrow={true}
        animation="shift"
        theme="dark"
        size="small"
        distance={8}
      >
        <div className="grid grid-cols-[5.5rem_1fr_auto] items-center gap-2 cursor-help">
          <span className="text-gray-500 dark:text-gray-400">Risk</span>
          <span
            className="h-2 min-w-[5rem] overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700"
            role="progressbar"
            aria-valuenow={score}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${tokenName || 'Token'} market risk score ${score} out of 100`}
          >
            <span
              className="block h-full rounded-full transition-all"
              style={{
                width: `${pct}%`,
                backgroundColor: riskScoreFillColor(score),
              }}
            />
          </span>
          <span className="text-right font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">{score}</span>
          <span className="col-start-2 col-span-2 -mt-1 text-xs text-gray-400 dark:text-gray-500">
            {interpretationLabel(riskScore.interpretation)}
            {relativeTime && relativeTime !== '-' && ` · ${relativeTime}`}
          </span>
        </div>
      </Tooltip>
    </div>
  );
};

export default RiskScoreBlock;
